<script lang="ts">
  import PageMeta from '$lib/components/PageMeta.svelte';
  import GridLines from '$lib/components/GridLines.svelte';
  import GridContent from '$lib/components/GridContent.svelte';
  import GridInner from '$lib/components/GridInner.svelte';
  import CornerFrame from '$lib/components/CornerFrame.svelte';
  import Star from '$lib/components/Star.svelte';
  import ContactCtaSection from '$lib/sections/ContactCtaSection.svelte';
  import { hatchStyle45 } from '$lib/styles/hatch';
  import { BarChart3, FileText, Bell, LayoutGrid, Users, ArrowUpDown } from '@lucide/svelte';

  const features = [
    { title: 'Real-time monitoring', description: 'Agent behavior, performance metrics, cost tracking — visible as it happens.', icon: BarChart3 },
    { title: 'Comprehensive logs', description: 'Every interaction, every tool call, every data access. Queryable and exportable.', icon: FileText },
    { title: 'Alerting', description: 'Anomalous activity, policy violations, cost thresholds — know before users complain.', icon: Bell },
    { title: 'Central catalog', description: 'Version, organize, and manage all agents from one place.', icon: LayoutGrid },
    { title: 'Role-based access', description: 'Entra ID / Azure AD integration. Your existing org structure, respected.', icon: Users },
    { title: 'Promotion flows', description: 'Sandbox → staging → production with admin approval gates at each step.', icon: ArrowUpDown },
  ];
</script>

<PageMeta
  title="Admin & Observability — See everything. Miss nothing."
  description="Real-time visibility into every agent, action, and data flow. Central catalog, role-based access, promotion flows, and cost tracking."
/>

<!-- Top stars -->
<div class="relative">
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] -top-px" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] -top-px" />
</div>

<!-- Hero -->
<section class="bg-[#F9F9F4] py-[80px] lg:py-[120px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px]">
        <h1 class="font-sans text-[48px] lg:text-[64px] tracking-[-0.04em] leading-[1.1] text-[#020202] mb-6">
          See everything. Miss nothing.
        </h1>
        <p class="font-mono font-light text-[16px] text-[#5C5855] leading-relaxed">
          Every agent, every action, every user, every data flow — visible in real time. Corral gives IT and security teams the governance layer they need before anyone in the organization can ship AI safely.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- §1: Narrative -->
<section class="bg-[#0d0d0d] py-[80px] relative">
  <GridLines variant="dark" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto mb-12">
        <h2 class="font-sans text-[36px] lg:text-[48px] text-white tracking-[-0.04em] leading-[1.1] mb-6">
          Manage your fleet of agents from a central catalog.
        </h2>
        <p class="font-body font-light text-[16px] text-[#878787] leading-relaxed">
          Monitor behavior and performance. Set alerts for anomalous activity. Promote agents from sandbox to production through a controlled approval flow. All of it integrated with your existing identity provider.
        </p>
      </div>

      <!-- Feature grid: 2 rows of 3 -->
      <div class="relative">
        <div class="hidden lg:block absolute top-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] top-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] top-[-9px]" />

        <div class="hidden lg:block absolute bottom-0 -left-[calc(54px+9.03vw)] -right-[calc(54px+9.03vw)] h-px bg-[#333335] z-[5]" aria-hidden="true"></div>
        <Star variant="full" class="hidden lg:block absolute -left-[63px] bottom-[-9px]" />
        <Star variant="full" class="hidden lg:block absolute -right-[63px] bottom-[-9px]" />

        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-0">
          {#each features as feature, i}
            <div class="bg-[#171719] border border-[#333335] p-6 {i % 3 !== 0 ? 'lg:-ml-px' : ''} {i >= 3 ? '-mt-px' : ''}">
              <CornerFrame
                class="w-[36px] h-[36px] flex items-center justify-center mb-4"
                bg="bg-transparent"
                borderColor="rgba(255, 255, 255, 0.2)"
                cornerColor="white"
                cornerSize={6}
              >
                <svelte:component this={feature.icon} class="w-4 h-4 text-white" />
              </CornerFrame>
              <h3 class="font-sans text-[16px] text-white mb-2">{feature.title}</h3>
              <p class="font-mono font-light text-[13px] text-[#878787] leading-relaxed">{feature.description}</p>
            </div>
          {/each}
        </div>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute top-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] top-0" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] top-0" />
</div>

<!-- §2: Entra ID callout -->
<section class="bg-[#f9f9f4] py-[80px] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <GridContent>
    <GridInner>
      <div class="max-w-[700px] mx-auto">
        <div class="border-l-2 border-[#ee6018] pl-6 py-2 mb-8">
          <p class="font-mono font-light text-[18px] text-[#020202] leading-relaxed">
            Entra ID native. Your existing org structure, your existing roles, your existing access policies — applied to every AI interaction automatically.
          </p>
        </div>
        <p class="font-body font-light text-[16px] text-[#5C5855] leading-relaxed">
          Usage analytics and cost optimization give you the data to demonstrate ROI. Admin approval gates give you the control to manage risk. One console for everything your organization is building with AI.
        </p>
      </div>
    </GridInner>
  </GridContent>
</section>

<!-- Divider: → CTA -->
<div class="h-[48px] bg-[#f9f9f4] relative" style={hatchStyle45}>
  <GridLines variant="light" />
  <div class="hidden lg:block absolute bottom-0 left-0 right-0 h-px bg-[#D3D3D3]" aria-hidden="true"></div>
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute left-[calc(9.03%-9px)] bottom-[-9px]" />
  <Star variant="bottom" color="#D3D3D3" class="hidden lg:block absolute right-[calc(9.03%-9px)] bottom-[-9px]" />
</div>

<!-- CTA -->
<ContactCtaSection />
